import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import VariantDisplay from '../components/VariantDisplay';
import ProductOptionsDisplay from '../components/CustomOptionsDisplay';
import StarRating from '../components/StarRating';
import { 
  ArrowLeft, Edit, Trash2, Calendar, MapPin, Users, Ticket,
  ChevronLeft, ChevronRight, User, CheckCircle, Shield, AlertCircle,
  Clock, DollarSign, Globe, Plus, Minus
} from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { getImageUrl, getFirstImage } from '../utils/imageHelper';
import ListingImage from '../components/ListingImage';

// ─── Ticket Tier Cards ────────────────────────────────────────────────────────
function TicketTierCards({ variants, variantCombinations, basePrice, capacity, registeredCount, onSelectionChange }) {
  const [quantities, setQuantities] = React.useState({});

  // Build tiers from variant combinations or single variant options
  const tiers = React.useMemo(() => {
    // Use combinations if available; fall back to first variant options
    if (variantCombinations?.length > 0) {
      return variantCombinations.map((c, i) => ({
        id: c.id || `combo-${i}`,
        name: [c.option1, c.option2, c.option3].filter(Boolean).join(' – ') || `Tier ${i + 1}`,
        price: parseFloat(c.price || basePrice || 0),
        available: c.available !== false,
        inventory: c.inventory_quantity,
      }));
    }
    const firstVariant = variants?.[0];
    if (!firstVariant?.options?.length) return [];
    return firstVariant.options.map((opt, i) => ({
      id: `opt-${i}`,
      name: opt.name || opt.label || opt.value || `Tier ${i + 1}`,
      price: parseFloat(opt.price_adjustment || 0) + parseFloat(basePrice || 0),
      available: true,
      inventory: null,
    }));
  }, [variants, variantCombinations, basePrice]);

  const total = React.useMemo(() =>
    Object.entries(quantities).reduce((sum, [tierId, qty]) => {
      const t = tiers.find(t => t.id === tierId);
      return sum + (t?.price || 0) * (qty || 0);
    }, 0), [quantities, tiers]);

  const totalQty = Object.values(quantities).reduce((s, v) => s + (v || 0), 0);

  if (!tiers.length) return null;

  return (
    <div className="space-y-3" data-testid="ticket-tier-cards">
      <div className="flex items-center gap-2">
        <Ticket className="h-4 w-4 text-indigo-600" />
        <h3 className="font-semibold text-sm text-gray-800">Select Ticket Type</h3>
      </div>

      <div className="space-y-2">
        {tiers.map((tier) => {
          const qty = quantities[tier.id] || 0;
          const seatsLeft = typeof tier.inventory === 'number' ? tier.inventory : null;
          const almostGone = seatsLeft !== null && seatsLeft <= 10;
          return (
            <div key={tier.id}
              className={`border-2 rounded-xl p-3 transition-all ${qty > 0 ? 'border-indigo-500 bg-indigo-50/40' : 'border-gray-200 bg-white'} ${!tier.available ? 'opacity-50' : ''}`}
              data-testid={`ticket-tier-${tier.id}`}>
              <div className="flex items-center justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-gray-900 truncate">{tier.name}</div>
                  <div className="text-base font-bold text-indigo-700">₹{tier.price.toLocaleString('en-IN')}</div>
                  {almostGone && (
                    <div className="text-xs text-red-500 font-medium mt-0.5">Only {seatsLeft} left!</div>
                  )}
                  {!tier.available && <div className="text-xs text-gray-400">Sold Out</div>}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button type="button" disabled={qty === 0 || !tier.available}
                    onClick={() => setQuantities(q => ({ ...q, [tier.id]: Math.max(0, (q[tier.id] || 0) - 1) }))}
                    className="w-8 h-8 rounded-full border-2 border-gray-300 flex items-center justify-center text-gray-600 hover:border-indigo-400 disabled:opacity-30">
                    <Minus className="h-3.5 w-3.5" />
                  </button>
                  <span className="w-6 text-center font-bold text-gray-900">{qty}</span>
                  <button type="button" disabled={!tier.available}
                    onClick={() => setQuantities(q => ({ ...q, [tier.id]: (q[tier.id] || 0) + 1 }))}
                    className="w-8 h-8 rounded-full border-2 border-indigo-400 bg-indigo-600 flex items-center justify-center text-white hover:bg-indigo-700 disabled:opacity-30">
                    <Plus className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {totalQty > 0 && (
        <div className="flex items-center justify-between bg-indigo-700 text-white rounded-xl px-4 py-3" data-testid="ticket-total-bar">
          <div>
            <div className="text-sm opacity-80">{totalQty} ticket{totalQty > 1 ? 's' : ''} selected</div>
            <div className="font-bold text-lg">₹{total.toLocaleString('en-IN')}</div>
          </div>
          <button type="button" disabled
            className="bg-white text-indigo-700 font-semibold text-sm px-4 py-2 rounded-lg opacity-80 cursor-not-allowed">
            Proceed
          </button>
        </div>
      )}
      {capacity > 0 && (
        <p className="text-xs text-gray-500 text-center">
          <Users className="h-3 w-3 inline mr-1" />{registeredCount}/{capacity} registered
        </p>
      )}
    </div>
  );
}

function SingleTicketWidget({ price, capacity, registeredCount }) {
  const [qty, setQty] = React.useState(1);
  return (
    <div className="rounded-xl border-2 border-indigo-100 bg-indigo-50/30 p-4 space-y-3" data-testid="single-ticket-widget">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-xs text-gray-500 font-medium uppercase tracking-wide">Ticket</div>
          <div className="text-xl font-bold text-gray-900">₹{price.toLocaleString('en-IN')}</div>
        </div>
        <div className="flex items-center gap-3">
          <button type="button" onClick={() => setQty(v => Math.max(1, v - 1))}
            className="w-8 h-8 rounded-full border-2 border-gray-300 flex items-center justify-center">
            <Minus className="h-3 w-3" />
          </button>
          <span className="font-bold w-5 text-center">{qty}</span>
          <button type="button" onClick={() => setQty(v => v + 1)}
            className="w-8 h-8 rounded-full border-2 border-indigo-500 bg-indigo-600 text-white flex items-center justify-center">
            <Plus className="h-3 w-3" />
          </button>
        </div>
      </div>
      <div className="flex items-center justify-between text-sm bg-white border border-gray-100 rounded-lg px-3 py-2">
        <span className="text-gray-500">Total</span>
        <span className="font-bold text-gray-900">₹{(price * qty).toLocaleString('en-IN')}</span>
      </div>
    </div>
  );
}

const EventDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [event, setEvent] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [deleting, setDeleting] = React.useState(false);
  const [selectedImage, setSelectedImage] = useState(0);
  const [variantSelections, setVariantSelections] = useState({});
  const [variantPriceAdjustment, setVariantPriceAdjustment] = useState(0);
  const [calculatedPrice, setCalculatedPrice] = useState(null);
  
  React.useEffect(() => {
    const fetchEvent = async () => {
      try {
        const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
        const token = sessionStorage.getItem('token');
        
        const response = await fetch(`${BACKEND_URL}/api/listings/${id}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (response.ok) {
          const data = await response.json();
          setEvent(data);
        } else {
          navigate('/listings/events');
        }
      } catch (error) {
        console.error('Error:', error);
        navigate('/listings/events');
      } finally {
        setLoading(false);
      }
    };
    
    if (id) fetchEvent();
  }, [id, navigate]);
  
  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this event?')) return;
    
    setDeleting(true);
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const token = sessionStorage.getItem('token');
      
      const response = await fetch(`${BACKEND_URL}/api/listings/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast({ title: 'Success', description: 'Event deleted successfully' });
        navigate('/listings/events');
      } else {
        toast({ title: 'Error', description: 'Failed to delete event', variant: 'destructive' });
      }
    } catch (error) {
      toast({ title: 'Error', description: 'Error deleting event', variant: 'destructive' });
    } finally {
      setDeleting(false);
    }
  };

  const handleVariantSelectionChange = ({ selections, priceAdjustment }) => {
    setVariantSelections(selections);
    setVariantPriceAdjustment(priceAdjustment || 0);
  };

  const nextImage = () => {
    if (event?.media?.length > 0) {
      setSelectedImage((prev) => (prev + 1) % event.media.length);
    }
  };

  const prevImage = () => {
    if (event?.media?.length > 0) {
      setSelectedImage((prev) => (prev - 1 + event.media.length) % event.media.length);
    }
  };

  const getDisplayPrice = () => {
    const basePrice = parseFloat(event?.data?.ticketPrice || 0);
    // If custom options have calculated a price, use that as base
    if (calculatedPrice !== null) {
      return calculatedPrice + variantPriceAdjustment;
    }
    return basePrice + variantPriceAdjustment;
  };

  const handlePriceChange = (newPrice) => {
    setCalculatedPrice(newPrice);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'TBA';
    return new Date(dateStr).toLocaleDateString('en-IN', {
      weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'
    });
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return '';
    return timeStr;
  };
  
  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-500">Loading...</div>
        </div>
      </DashboardLayout>
    );
  }
  
  if (!event) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-500">Event not found</div>
        </div>
      </DashboardLayout>
    );
  }
  
  const data = event.data || {};
  const images = event.media || [];
  const basePrice = parseFloat(data.ticketPrice || 0);
  
  // Handle back navigation - go to previous page or fallback to all listings
  const handleBack = () => {
    // Check if there's history to go back to
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/listings/products');
    }
  };
  
  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6 px-4">
          <Button variant="ghost" onClick={handleBack} className="gap-2">
            <ArrowLeft className="h-4 w-4" />
            Back
          </Button>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => navigate(`/listings/events/edit/${id}`)}>
              <Edit className="h-4 w-4 mr-2" />
              Edit
            </Button>
            <Button variant="destructive" onClick={handleDelete} disabled={deleting}>
              <Trash2 className="h-4 w-4 mr-2" />
              {deleting ? 'Deleting...' : 'Delete'}
            </Button>
          </div>
        </div>

        {/* Two Column Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 px-4">
          
          {/* LEFT COLUMN - Gallery */}
          <div className="space-y-4">
            {/* Main Image */}
            <div className="relative bg-white rounded-xl overflow-hidden border aspect-video">
              {images.length > 0 ? (
                <>
                  <ListingImage src={images[selectedImage]} alt={event.title} className="w-full h-full object-cover" showPlaceholder={false} />
                  {images.length > 1 && (
                    <>
                      <button onClick={prevImage} className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white rounded-full p-2 shadow-lg">
                        <ChevronLeft className="h-5 w-5" />
                      </button>
                      <button onClick={nextImage} className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white rounded-full p-2 shadow-lg">
                        <ChevronRight className="h-5 w-5" />
                      </button>
                    </>
                  )}
                </>
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-gray-100">
                  <Calendar className="h-24 w-24 text-gray-300" />
                </div>
              )}
            </div>

            {/* Thumbnails */}
            {images.length > 1 && (
              <div className="flex gap-2 overflow-x-auto pb-2">
                {images.map((url, index) => (
                  <button
                    key={url.id || url.name || `${url.title || "url"}-${index}`}
                    onClick={() => setSelectedImage(index)}
                    className={`flex-shrink-0 w-20 h-14 rounded-lg overflow-hidden border-2 transition-all ${selectedImage === index ? 'border-blue-600 ring-2 ring-blue-200' : 'border-gray-200 hover:border-gray-300'}`}
                  >
                    <ListingImage src={url} alt={`Thumbnail ${index + 1}`} className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}

            {/* Tabs */}
            <Tabs defaultValue="description" className="w-full">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="description">Description</TabsTrigger>
                <TabsTrigger value="schedule">Schedule</TabsTrigger>
                <TabsTrigger value="policies">Policies</TabsTrigger>
              </TabsList>
              
              <TabsContent value="description" className="mt-4">
                <Card>
                  <CardContent className="pt-4">
                    <p className="text-gray-700 whitespace-pre-wrap leading-relaxed">{event.description}</p>
                    {data.highlights && (
                      <div className="mt-4 pt-4 border-t">
                        <h4 className="font-medium mb-2">Highlights</h4>
                        <p className="text-sm text-gray-600 whitespace-pre-wrap">{data.highlights}</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
              
              <TabsContent value="schedule" className="mt-4">
                <Card>
                  <CardContent className="pt-4 space-y-4">
                    <div className="flex items-start gap-3">
                      <Calendar className="h-5 w-5 text-blue-500 mt-0.5" />
                      <div>
                        <h4 className="font-medium">Date</h4>
                        <p className="text-sm text-gray-600">{formatDate(data.eventDate)}</p>
                        {data.endDate && <p className="text-sm text-gray-500">Ends: {formatDate(data.endDate)}</p>}
                      </div>
                    </div>
                    {(data.startTime || data.endTime) && (
                      <div className="flex items-start gap-3">
                        <Clock className="h-5 w-5 text-blue-500 mt-0.5" />
                        <div>
                          <h4 className="font-medium">Time</h4>
                          <p className="text-sm text-gray-600">
                            {formatTime(data.startTime)}{data.endTime && ` - ${formatTime(data.endTime)}`}
                          </p>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
              
              <TabsContent value="policies" className="mt-4">
                <Card>
                  <CardContent className="pt-4 space-y-4">
                    {data.cancellationPolicy && (
                      <div>
                        <h4 className="font-medium mb-2">Cancellation Policy</h4>
                        <p className="text-sm text-gray-600 whitespace-pre-wrap">{data.cancellationPolicy}</p>
                      </div>
                    )}
                    {data.terms && (
                      <div>
                        <h4 className="font-medium mb-2">Terms & Conditions</h4>
                        <p className="text-sm text-gray-600 whitespace-pre-wrap">{data.terms}</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </div>

          {/* RIGHT COLUMN - Info & Configuration */}
          <div className="space-y-6">
            {/* Title & Status */}
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Badge variant={event.status === 'active' ? 'default' : 'secondary'}>{event.status}</Badge>
                {data.eventType && <Badge variant="outline" className="capitalize">{data.eventType}</Badge>}
                {data.isFree && <Badge className="bg-green-600">Free Entry</Badge>}
              </div>
              <h1 className="text-2xl font-bold text-gray-900">{event.title}</h1>
              <div className="mt-2">
                <StarRating listingId={id} size="md" showCount={true} fetchData={true} />
              </div>
            </div>

            {/* Organizer Info */}
            <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
              <div className="h-10 w-10 rounded-full bg-indigo-100 flex items-center justify-center">
                <User className="h-5 w-5 text-indigo-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Organizer</p>
                <p className="font-medium">{data.organizer || event.vendor_name || 'Organizer'}</p>
              </div>
            </div>

            {/* Date & Location Summary */}
            <Card className="bg-blue-50 border-blue-200">
              <CardContent className="pt-4 space-y-3">
                <div className="flex items-center gap-3">
                  <Calendar className="h-5 w-5 text-blue-600" />
                  <div>
                    <p className="font-medium">{formatDate(data.eventDate)}</p>
                    {data.startTime && <p className="text-sm text-gray-600">{formatTime(data.startTime)}</p>}
                  </div>
                </div>
                {(data.venue || data.address) && (
                  <div className="flex items-center gap-3">
                    <MapPin className="h-5 w-5 text-blue-600" />
                    <div>
                      <p className="font-medium">{data.venue || 'Venue'}</p>
                      {data.address && <p className="text-sm text-gray-600">{data.address}</p>}
                    </div>
                  </div>
                )}
                {data.eventMode && (
                  <div className="flex items-center gap-3">
                    <Globe className="h-5 w-5 text-blue-600" />
                    <p className="font-medium capitalize">{data.eventMode}</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Pricing */}
            {!data.isFree && (
              <div className="bg-gray-50 rounded-xl p-4">
                <div className="flex items-baseline gap-2">
                  <span className="text-3xl font-bold text-gray-900">₹{getDisplayPrice().toLocaleString()}</span>
                  <span className="text-lg text-gray-500">/ticket</span>
                </div>
                {variantPriceAdjustment > 0 && (
                  <p className="text-sm text-green-600 mt-1">+₹{variantPriceAdjustment.toLocaleString()} for selected options</p>
                )}
                {data.capacity && (
                  <p className="text-sm text-gray-500 mt-2">
                    <Users className="h-4 w-4 inline mr-1" />
                    {data.registeredCount || 0} / {data.capacity} registered
                  </p>
                )}
              </div>
            )}

            {/* Ticket Variants — shown as tier cards if variants exist */}
            {(event.variants?.length > 0 || event.data?.variants?.length > 0) ? (
              <TicketTierCards
                variants={event.variants?.length > 0 ? event.variants : (event.data?.variants || [])}
                variantCombinations={event.variant_combinations?.length > 0 ? event.variant_combinations : (event.data?.variant_combinations || event.data?.variantCombinations || [])}
                basePrice={basePrice}
                capacity={data.capacity}
                registeredCount={data.registeredCount || 0}
                onSelectionChange={handleVariantSelectionChange}
              />
            ) : !data.isFree && (
              /* No variants — single ticket type with quantity stepper */
              <SingleTicketWidget
                price={getDisplayPrice()}
                capacity={data.capacity}
                registeredCount={data.registeredCount || 0}
              />
            )}

            {/* Custom Options Display - shown for all listing types */}
            {(event.has_custom_options || event.data?.hasCustomOptions || event.data?.customOptionsConfig?.options?.length > 0 || event.data?.productOptionsConfig?.options?.length > 0) && (
              <ProductOptionsDisplay 
                listingId={id}
                basePrice={basePrice}
                variantSelections={variantSelections}
                variantPriceAdjustment={variantPriceAdjustment}
                onPriceChange={handlePriceChange}
              />
            )}

            {/* CTA Buttons */}
            <div className="space-y-3">
              <Button className="w-full h-12 text-lg bg-gray-900 hover:bg-gray-800" disabled>
                <Ticket className="h-5 w-5 mr-2" />
                {data.isFree ? 'REGISTER NOW' : 'BUY TICKETS'}
              </Button>
              <Button variant="outline" className="w-full h-12 text-lg" disabled>
                <Calendar className="h-5 w-5 mr-2" />
                ADD TO CALENDAR
              </Button>
              <p className="text-xs text-center text-gray-500">(Preview mode - Buttons disabled in admin view)</p>
            </div>

            {/* Trust Badges */}
            <div className="grid grid-cols-3 gap-4 pt-4 border-t">
              <div className="text-center">
                <CheckCircle className="h-6 w-6 mx-auto text-gray-600 mb-1" />
                <p className="text-xs text-gray-600">Verified</p>
              </div>
              <div className="text-center">
                <Shield className="h-6 w-6 mx-auto text-gray-600 mb-1" />
                <p className="text-xs text-gray-600">Secure</p>
              </div>
              <div className="text-center">
                <AlertCircle className="h-6 w-6 mx-auto text-gray-600 mb-1" />
                <p className="text-xs text-gray-600">Support</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default EventDetails;